import {useSyncExternalStore} from 'react'

/**
 * Screen captures uploaded during a turn (AB#6762), shown in the chat the moment
 * the upload succeeds, while Alpinist is still working. They live as object URLs
 * until the turn's answer arrives:
 * - an answer that carries the capture as an image attachment replaces them,
 *   and they are revoked;
 * - an answer without one (the live event did not include it) keeps them, bound
 *   to that answer, until the chat unmounts and the history reload shows the
 *   persisted attachment instead.
 */

export type LiveCapture = {requestId: string; url: string}

const EMPTY: readonly LiveCapture[] = []
// Keyed by conversation id while the turn runs, then by answer message id.
const pending = new Map<string, readonly LiveCapture[]>()
const bound = new Map<string, readonly LiveCapture[]>()
const listeners = new Set<() => void>()

const notify = (): void => listeners.forEach((listener) => listener())
const revoke = (captures: readonly LiveCapture[] | undefined): void =>
  captures?.forEach((capture) => URL.revokeObjectURL(capture.url))

export const addLiveCapture = (conversationId: string, requestId: string, image: Blob): void => {
  const current = pending.get(conversationId) ?? EMPTY
  if (current.some((capture) => capture.requestId === requestId)) return
  pending.set(conversationId, [...current, {requestId, url: URL.createObjectURL(image)}])
  notify()
}

/** The turn's answer arrived: drop the live captures, or keep them on the answer. */
export const settleLiveCaptures = (
  conversationId: string,
  answerId: string,
  answerHasImage: boolean,
): void => {
  const current = pending.get(conversationId)
  if (!current) return
  pending.delete(conversationId)
  if (answerHasImage) revoke(current)
  else bound.set(answerId, current)
  notify()
}

/** Revoke everything kept for a conversation (the chat unmounted or switched). */
export const clearLiveCaptures = (conversationId: string, answerIds: readonly string[]): void => {
  revoke(pending.get(conversationId))
  pending.delete(conversationId)
  answerIds.forEach((id) => {
    revoke(bound.get(id))
    bound.delete(id)
  })
  notify()
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Captures of the turn still running in this conversation. */
export const usePendingCaptures = (conversationId: string): readonly LiveCapture[] =>
  useSyncExternalStore(subscribe, () => pending.get(conversationId) ?? EMPTY)

/** Captures shown on an answer whose live event came without the attachment. */
export const useBoundCaptures = (answerId: string): readonly LiveCapture[] =>
  useSyncExternalStore(subscribe, () => bound.get(answerId) ?? EMPTY)
